"use client";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useRef } from "react";
import type {
    LobbySettings,
} from "@rosette/ts/src/business/game/LobbySettings.js";
import { DeviceInfoProvider } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import { AssetProvider } from "@/components/assets/AssetContext.js";
import GameUI from "@/components/game/GameUI.js";
import { EnvProvider } from "@/components/env/EnvContext.js";
import type { APIAppEnv } from "@rosette/ts/src/business/api/api_schema.js";
import { toCompleteUser } from "@rosette/ts/src/business/api/api_schema.js";
import type { GameSource } from "@rosette/ts/src/business/game/controller/source/GameSource.js";
import { createGameSource } from "@/components/game/GameUIContext.js";
import { useGameAPI, GameAPIProvider } from "@/components/api/GameAPIContext.js";
import { useGamePreferences } from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import { useSyncGameSource } from "@/app/game/useSyncGameSource.js";
import { useMutation } from "@tanstack/react-query";
import { startGame } from "@/app/game/startGame.js";
import { RemoteLobbySettings } from "@rosette/ts/src/business/game/RemoteLobbySettings.js";
import { showNotification } from "@rosette/web_utils/src/game/notifications.js";


interface LobbyPageContentProps {
    appEnv: APIAppEnv;
    lobbyID: string;
}


function LobbyPageContentInternal({ appEnv, lobbyID }: LobbyPageContentProps) {
    const router = useRouter();
    const completeUser = toCompleteUser(appEnv.user);
    const gameAPI = useGameAPI();
    const { preferences, savedPreferences } = useGamePreferences(completeUser);

    const clientControls: GameClientControls = useMemo(() => {
        const missing = () => {
            throw new Error("This function is missing!");
        };
        return {
            startGame: missing,
            startingGame: false,
            startGameError: null,
            clearError: missing,
            resignGame: missing,
            abortGame: missing,
            notify: showNotification,
        };
    }, []);

    const gameSourceRef = useRef<GameSource | null>(null);
    if (gameSourceRef.current === null || gameSourceRef.current.getSetupSettings().getLobbyID() !== lobbyID) {
        const settings = new RemoteLobbySettings(lobbyID);
        gameSourceRef.current = createGameSource(completeUser, settings, clientControls, preferences, gameAPI);
    }
    const gameSource = gameSourceRef.current;

    const startGameMutation = useMutation({
        mutationFn: async (settings: LobbySettings) => {
            await startGame(router, completeUser, settings, gameAPI);
        },
    });
    clientControls.startGame = settings => startGameMutation.mutate(settings);
    clientControls.startingGame = startGameMutation.isPending;
    clientControls.startGameError = startGameMutation.error;
    clientControls.clearError = () => startGameMutation.reset();
    clientControls.resignGame = useCallback(() => {
        if (!gameSource)
            throw new Error("There is no game active");

        gameSource.handleResign();
    }, [gameSource]);
    clientControls.abortGame = useCallback((path) => {
        if (gameSource) {
            gameSource.recordAbortGame();
        }
        // Don't use the router as sometimes it doesn't refresh properly.
        window.onbeforeunload = null;
        window.location.href = (path ? path.toString() : "/");
    }, [gameSource]);
    useSyncGameSource(gameSource, clientControls, preferences, savedPreferences);

    return (
        <div className="fixed inset-0 w-full h-full">
            <GameUI
                gameSource={gameSource}
                clientControls={clientControls} />
        </div>
    );
}


export default function LobbyPageContent({ appEnv, lobbyID }: LobbyPageContentProps) {
    return (
        <EnvProvider appEnv={appEnv}>
            <GameAPIProvider>
                <DeviceInfoProvider>
                    <AssetProvider>
                        <LobbyPageContentInternal
                            appEnv={appEnv}
                            lobbyID={lobbyID} />
                    </AssetProvider>
                </DeviceInfoProvider>
            </GameAPIProvider>
        </EnvProvider>
    );
}
